import {
  Accordion,
  Box,
  BoxProps,
  Heading,
  HeadingProps,
  StackProps,
  Text,
  TextProps,
  VStack,
} from '@chakra-ui/react'
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider'
import React from 'react'

export interface GenericFAQItem {
  question: string
  answer: React.ReactNode
}

export interface GenericFAQProps {
  heading: string
  subheading?: string
  items: GenericFAQItem[]
  /** Section background. Defaults to `colorPalette.panel`. */
  background?: BoxProps['background']
  colorPalette?: BoxProps['colorPalette']
  containerProps?: BoxProps
  headerProps?: StackProps
  headingProps?: HeadingProps
  subheadingProps?: TextProps
  accordionProps?: Accordion.RootProps
  questionProps?: TextProps
  answerProps?: TextProps
}

/**
 * GenericFAQ – A full-page landing block with a centered heading/subheading and
 * an accordion of FAQ items provided via props.
 */
export const GenericFAQ: React.FC<GenericFAQProps> = ({
  heading,
  subheading,
  items,
  background = 'colorPalette.bg',
  colorPalette,
  containerProps,
  headerProps,
  headingProps,
  subheadingProps,
  accordionProps,
  questionProps,
  answerProps,
}) => {
  const { getBreakpointValue } = useBreakpointContext()

  return (
    <Box
      as={'section'}
      background={background}
      colorPalette={colorPalette}
      paddingX={getBreakpointValue([4, 6, 8])}
      paddingY={getBreakpointValue([12, 12, 14, 14, 20, 24])}
      width={'full'}
      {...containerProps}
    >
      <VStack
        align={'center'}
        gap={getBreakpointValue([8, 8, 10, 12])}
        marginX={'auto'}
        maxWidth={'container.lg'}
        width={'full'}
      >
        <VStack
          align={'center'}
          gap={getBreakpointValue([2, 2, 4])}
          textAlign={'center'}
          width={'full'}
          {...headerProps}
        >
          <Heading
            as={'h2'}
            size={getBreakpointValue(['2xl', '3xl', '4xl', '4xl', '5xl'])}
            textAlign={'center'}
            {...headingProps}
          >
            {heading}
          </Heading>
          {subheading && (
            <Text
              color={'colorPalette.fg/70'}
              fontSize={getBreakpointValue(['md', 'lg', 'xl', 'xl', '2xl'])}
              fontWeight={'medium'}
              {...subheadingProps}
            >
              {subheading}
            </Text>
          )}
        </VStack>

        <Accordion.Root
          collapsible
          multiple
          defaultValue={[]}
          variant={'outline'}
          width={'full'}
          {...accordionProps}
        >
          {items.map((item, index) => (
            <Accordion.Item
              key={`${item.question}-${index}`}
              _hover={{ borderColor: 'colorPalette.border' }}
              _open={{ borderColor: 'colorPalette.border' }}
              bgColor={'colorPalette.muted'}
              border={'1px solid transparent'}
              borderRadius={'lg'}
              marginBottom={2}
              value={`faq-${index}`}
            >
              <Accordion.ItemTrigger _expanded={{ paddingBottom: 2 }} padding={6}>
                <Text
                  color={'colorPalette.fg'}
                  flex={1}
                  fontSize={getBreakpointValue(['md', 'lg'])}
                  fontWeight={600}
                  textAlign={'left'}
                  {...questionProps}
                >
                  {item.question}
                </Text>
                <Accordion.ItemIndicator />
              </Accordion.ItemTrigger>
              <Accordion.ItemContent paddingBottom={6} paddingX={6}>
                <Accordion.ItemBody>
                  <Text
                    color={'colorPalette.fg'}
                    fontSize={getBreakpointValue(['md', 'lg'])}
                    {...answerProps}
                  >
                    {item.answer}
                  </Text>
                </Accordion.ItemBody>
              </Accordion.ItemContent>
            </Accordion.Item>
          ))}
        </Accordion.Root>
      </VStack>
    </Box>
  )
}

export default GenericFAQ
